import React, { useState, useCallback } from 'react';
import { ArrowRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { isValidLinkedinUrl } from '@/utils/chatbotRecommenderUtils';
import recommenderImage from '/src/assets/recommenderImage2.webp';

interface LinkedInInputProps {
  onSubmit: (linkedinUrl: string) => void;
  onSkip: () => void;
}

export function LinkedInInput({ onSubmit, onSkip }: LinkedInInputProps) {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback((e?: React.FormEvent) => {
    e?.preventDefault();
    
    const trimmedValue = inputValue.trim();
    
    // Si está vacío, hacer skip
    if (!trimmedValue) {
      onSkip();
      return;
    }
    
    // Validar URL de LinkedIn
    if (!isValidLinkedinUrl(trimmedValue)) {
      setError('Por favor, ingresa un enlace válido de LinkedIn');
      return;
    }
    
    // Submit válido
    setError(null);
    onSubmit(trimmedValue);
  }, [inputValue, onSubmit, onSkip]);

  const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    if (error) setError(null);
  }, [error]);

  return (
    <div className="flex flex-col items-center justify-center h-full px-8 py-12 animate-in fade-in-50 duration-300">
      {/* Image */}
      <div className="mb-6">
        <img 
          src={recommenderImage} 
          alt="Recomendador" 
          className="w-40 h-40 object-contain"
        />
      </div>
      
      {/* Content */}
      <h2 className="text-2xl font-semibold text-foreground mb-3 text-center">
        Optimiza tu recomendación
      </h2>
      
      <p className="text-center text-muted-foreground text-sm max-w-sm mb-8 leading-relaxed">
        Ingresa tu perfil de LinkedIn para recibir recomendaciones personalizadas, o continúa sin él.
      </p>
      
      {/* Input Form */}
      <form onSubmit={handleSubmit} className="w-full max-w-md space-y-3">
        <div className="relative">
          <input
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyPress={handleKeyPress}
            placeholder="linkedin.com/in/tu-perfil"
            className={cn(
              'w-full px-4 py-3 pr-11 rounded-lg',
              'bg-background border',
              'text-sm text-foreground placeholder:text-muted-foreground',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent',
              'transition-all',
              error ? 'border-destructive' : 'border-input'
            )}
          />
          <button
            type="submit"
            className={cn(
              'absolute right-1.5 top-1/2 -translate-y-1/2',
              'h-8 w-8 rounded-md',
              'bg-primary text-primary-foreground',
              'hover:bg-primary/90 active:scale-95',
              'transition-all',
              'flex items-center justify-center'
            )}
          >
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        
        {/* Error Message */}
        {error && (
          <p className="text-xs text-destructive animate-in slide-in-from-top-1">
            {error}
          </p>
        )}
      </form>
      
      {/* Skip Button */}
      <button
        onClick={onSkip}
        className={cn(
          'mt-6 text-sm text-muted-foreground hover:text-foreground',
          'transition-colors underline-offset-4 hover:underline'
        )}
      >
        Continuar sin LinkedIn
      </button>
    </div>
  );
}

